import { useEffect, useState } from 'react';
import { message } from 'antd';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { resetLocation } from '../../../redux/slices/locationSlice';
import { setTaxEstimateAddress } from '../../../redux/slices/shoppingCartSlice';
import { setSelectedAddress } from '../../../redux/slices/userSlice';
import { deleteShippingAddress, fetchCountries, fetchUserShip } from '../../../services';
import { useShippingData } from '../../../hooks/userShippingData';
import { useShippingUpdate } from '../../../hooks/userShippingUpdate';
import { createEmptyAddress } from './createEmptyAddress';

export const useShippingAddressForm = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const shipping = useAppSelector((state) => state.user.shipping);
  const { countries, provinces, loading: locationLoading } = useAppSelector((state) => state.location);
  const isCheckoutPage = location.pathname.includes('/cart/checkout');
  const [userInfo, setUserInfo] = useState(createEmptyAddress);
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [isCreatingNewAddress, setIsCreatingNewAddress] = useState(false);

  const { loading: loadingData } = useShippingData(
    location,
    setUserInfo,
    isCreatingNewAddress
  );
  const {
    handleUpdateProfile,
    loading: loadingUpdate,
    updateButtonClicked,
    addressValidationMessage,
    setAddressValidationMessage,
  } = useShippingUpdate(
    userInfo,
    setErrors,
    isCreatingNewAddress
  );

  const loading = loadingData || loadingUpdate;
  const buttonText = isCheckoutPage ? 'Next' : 'Update';

  useEffect(() => {
    if (isCreatingNewAddress || !shipping) {
      return;
    }

    setUserInfo((prev) => ({
      ...prev,
      ...shipping,
    }));
  }, [shipping, isCreatingNewAddress]);

  useEffect(() => {
    if (countries.length > 0 || locationLoading) {
      return;
    }

    dispatch(fetchCountries());
  }, [countries.length, dispatch, locationLoading]);

  const handleCountryChange = (country: string) => {
    const selectedCountry = countries.find((item) => item.name === country);
    const selectedProvince = provinces.find((province) => province.country?.id === selectedCountry?.id);

    setAddressValidationMessage(null);
    if (isCheckoutPage) {
      dispatch(setTaxEstimateAddress({
        country,
        province: '',
      }));
    }
    setUserInfo((prev) => ({
      ...prev,
      country,
      province: '',
      phoneCode: selectedCountry?.phone_code || selectedProvince?.country?.phone_code || prev.phoneCode,
      emoji: selectedCountry?.emoji || selectedProvince?.country?.emoji || prev.emoji,
    }));
  };

  const handleProvinceChange = (province: string) => {
    const selectedProvince = provinces.find((item) => item.name === province);

    setAddressValidationMessage(null);
    if (isCheckoutPage) {
      dispatch(setTaxEstimateAddress({
        province,
        country: userInfo.country || 'USA',
      }));
    }
    setUserInfo((prev) => ({
      ...prev,
      province,
      phoneCode: selectedProvince?.country?.phone_code || prev.phoneCode,
      emoji: selectedProvince?.country?.emoji || prev.emoji,
    }));
  };

  const handleAddressChange = (address: number, exists: boolean) => {
    if (!exists) {
      setUserInfo(createEmptyAddress());
      setIsCreatingNewAddress(true);
      dispatch(resetLocation());
      dispatch(setSelectedAddress(address));
      return;
    }

    setIsCreatingNewAddress(false);
    dispatch(setSelectedAddress(address));
  };

  const handlePrimaryAction = async () => {
    const success = await handleUpdateProfile({ navigateOnSuccess: isCheckoutPage });
    if (success) {
      setIsCreatingNewAddress(false);
    }
  };

  const handleDeleteCurrentAddress = async () => {
    if (!shipping?.id) {
      return;
    }

    try {
      await dispatch(deleteShippingAddress({ where: { id: shipping.id } })).unwrap();
      dispatch(resetLocation());
      dispatch(setSelectedAddress(0));
      setIsCreatingNewAddress(false);

      try {
        await dispatch(
          fetchUserShip({
            currentPage: location.pathname,
            selectedAddress: 0,
          })
        ).unwrap();
      } catch {
        setUserInfo(createEmptyAddress());
      }

      message.success('Shipping address deleted.');
    } catch (error) {
      message.error(typeof error === 'string' ? error : 'Failed to delete shipping address.');
    }
  };

  return {
    addressValidationMessage,
    buttonText,
    errors,
    handleAddressChange,
    handleDeleteCurrentAddress,
    handlePrimaryAction,
    handleCountryChange,
    handleProvinceChange,
    isCheckoutPage,
    loading,
    setAddressValidationMessage,
    setUserInfo,
    shipping,
    updateButtonClicked,
    userInfo,
  };
};
